import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { getContext } from '@fiverr-private/fiverr_context';
import SellerCard from '@fiverr-private/seller_card/src/SellerCard';
import { PortfolioPresence } from '@fiverr-private/portfolio_showcase';
import { NotableClientsBar, NotableClientsAbout } from '@fiverr-private/seller_presence';
import FiverrLearn from '@fiverr-private/fiverr_learn/src/components';
import ProVerified from '@fiverr-private/pro_verified/src/components';
import { PUMAS_NOTABLE_CLIENTS_SELLER_PAGE } from '../../../experiments/experimentsEnum';
import { inExperiment } from '../../../experiments';
import { buildPortfolioData, projectsShown } from '../lib/dataBuilder';
import WithImpressions from '../WithImpressions';
import { MODAL_WRAPPER_CLASS } from '../constants';
import { FirstMessagePopup } from '../FirstMessagePopup';
import CouponBomba from '../CouponBomba';
import { notableClientsBarEvent, notableClientsAboutEvent } from '../../../common/impressions_data';
import LoyaltySellerBanner from './LoyaltyBanner';
import SellerProfile from './SellerProfile';
import ScrollTo from './scrollTo';
import { biHelper } from './biHelper';
import WalkthroughNotableClients from './WalkthroughNotableClients';

import './index.scss';

const InfoPanel = ({
    sellerCard = {},
    proSubCategories = [],
    portfolio = {},
    notableClients = [],
    fiverrLearnCourses = [],
    sellerProfile = {},
    skillTests,
    scrollComponents,
    pageCtxId = '',
    currentUserNumberedId: buyerId,
    coupon,
    repeatScore,
    handleLoyaltyBannerView
}) => {
    const { locale, abTests, userId } = getContext();
    const [showFirstMessagePopup, setShowFirstMessagePopup] = useState(false);
    const showNotableClients = inExperiment(PUMAS_NOTABLE_CLIENTS_SELLER_PAGE, abTests) &&
        !!notableClients.length;
    const portfolioProjects = buildPortfolioData(portfolio, sellerCard.user);
    const hasPortfolioProjects = !!portfolioProjects.projectList.length;
    const { id: sellerId, is_pro: isPro, username, profile_photo } = sellerCard.user;
    let aboutRef;
    let position = 0;

    const baseBiData = {
        sellerId,
        buyerId,
        pageCtxId
    };

    const showLoyaltySellerBannerExperiment = () => {
        const { isMobile } = getContext();

        return !isMobile && !_.isEmpty(repeatScore);
    };
    const handleContactClick = (event) => {
        event.preventDefault();
        setShowFirstMessagePopup(true);
    };
    const handleCloseFirstMessagePopup = () => setShowFirstMessagePopup(false);

    isPro && position++;
    const pro_skills_impression = biHelper({
        ...baseBiData,
        position,
        data: proSubCategories,
        group: 'seller_card_fiverr_pro_skills_verified_impression'
    });

    position++; // increase value for the seller card
    showNotableClients && position++;
    const notableClientsProps = {
        notableClients,
        seller: {
            id: sellerId,
            name: username,
            profilePhoto: profile_photo
        },
        origin: 'seller_page',
        isSlimDesign: true,
        wrap: true
    };
    hasPortfolioProjects && position++;
    const portfolioImpression = {
        position,
        type: 'seller_portfolio_component_is_shown',
        entity: 'client',
        user: {
            id: userId
        },
        seller: {
            id: sellerId
        },
        group: 'seller_portfolio',
        page: {
            name: portfolioProjects.origin,
            element: {
                name: 'number_of_seller_projects_that_shown',
                metadata: projectsShown(portfolioProjects.projectList)
            }
        }
    };

    !_.isEmpty(fiverrLearnCourses) && position++;
    const fiverr_learn_impression = biHelper({
        ...baseBiData,
        position,
        data: fiverrLearnCourses,
        group: 'seller_card_fiverr_learn_impression'
    });

    const campaignLinkParams = `referrer=seller_card&ref_ctx_id=${pageCtxId}`;

    const showScrollTo = scrollComponents && !_.isEmpty(scrollComponents);

    return (
        <div className="info-panel col-12-xs col-5-md col-4-lg">

            {isPro &&
                <WithImpressions pageCtxId={pageCtxId} value={pro_skills_impression}>
                    <ProVerified linkParams={campaignLinkParams}
                        pro_sub_categories={proSubCategories}
                        locale={locale}/>
                </WithImpressions>
            }
            <SellerCard {...sellerCard} modalWrapperClass={MODAL_WRAPPER_CLASS} locale={locale} onContactClick={handleContactClick}>
                { showNotableClients && (
                    <div className="m-t-24 p-t-24 p-b-32 notable-clients-bar-wrapper">
                        <WithImpressions pageCtxId={pageCtxId} value={notableClientsBarEvent(userId, sellerId)}>
                            <div className="selected-notable-clients">
                                <NotableClientsBar {...notableClientsProps}/>
                            </div>
                        </WithImpressions>
                    </div>
                )}
            </SellerCard>
            { showNotableClients && <WalkthroughNotableClients sellerName={username}/> }
            {<FirstMessagePopup onClose={handleCloseFirstMessagePopup} modalTriggered={showFirstMessagePopup} sellerName={username}/>}
            {showLoyaltySellerBannerExperiment() &&
                <LoyaltySellerBanner sellerName={username}
                    sellerId={sellerId}
                    handleLoyaltyBannerView={handleLoyaltyBannerView}/>
            }

            {coupon && coupon.discountValue && <CouponBomba className="mobile"
                ignoreOnMountEvents
                coupon={coupon}
                sellerId={sellerId}
                userId={buyerId}/>}

            {showScrollTo &&
                <ScrollTo scrollComponents={scrollComponents}
                    aboutScrollFunc={() => aboutRef.scrollIntoView({ block: 'start', behavior: 'smooth' })}/>
            }

            {showScrollTo &&
                <div ref={(ref) => (aboutRef = ref)}/>
            }

            {hasPortfolioProjects &&
                <WithImpressions pageCtxId={pageCtxId} value={portfolioImpression}>
                    <PortfolioPresence {...portfolioProjects}/>
                </WithImpressions>
            }

            { showNotableClients && (
                <div className="notable-clients-about-wrapper">
                    <WithImpressions pageCtxId={pageCtxId} value={notableClientsAboutEvent(userId, sellerId)}>
                        <NotableClientsAbout {...notableClientsProps}/>
                    </WithImpressions>
                </div>
            )}

            {!_.isEmpty(fiverrLearnCourses) &&
                <WithImpressions pageCtxId={pageCtxId} value={fiverr_learn_impression}>
                    <FiverrLearn courses={fiverrLearnCourses} selfview={false}/>
                </WithImpressions>
            }

            <SellerProfile {...sellerProfile}
                skillTests={skillTests}
                showProjectsAndClients={!showNotableClients}
                biData={{ ...baseBiData, initialCounter: position }}/>
        </div>
    );
};

InfoPanel.propTypes = {
    sellerCard: PropTypes.object,
    portfolio: PropTypes.object,
    notableClients: PropTypes.array,
    fiverrLearnCourses: PropTypes.array,
    proSubCategories: PropTypes.array,
    sellerProfile: PropTypes.object,
    scrollComponents: PropTypes.array,
    pageCtxId: PropTypes.string,
    coupon: PropTypes.shape({
        couponId: PropTypes.string.isRequired,
        couponType: PropTypes.string.isRequired,
        code: PropTypes.string.isRequired,
        discountValue: PropTypes.number.isRequired,
        providerName: PropTypes.string.isRequired
    })
};

export default InfoPanel;
